import type { FC, CSSProperties, MouseEventHandler } from 'react';
import { useMemo, useState, useCallback, memo } from 'react';
import classNames from 'classnames';

import { Image } from 'antd-mobile';

import type { ProductInfoComboItem, ProductInfoComboProductType, ProductInfoCustomization } from '@/apis/product/types';
import { getCDNAssets } from '@/utils';
import { getExtraCustomizationCacheKey } from '../useEditor';
import { getCustomizationItemsTotalPrice } from '../func';
import { checkSingleComboProductIsDefault } from '../../utils';

import styles from './styles/combodetail.module.less';

export const MAX_DISPLAY_COMBO_LENGTH = 6;

export const PriceNode: FC<{ diffPrice?: number; extraPrice?: number; symbol?: string }> = (props) => {
  const { diffPrice, extraPrice = 0, symbol = '¥' } = props;

  const [unit, priceTxt] = useMemo(() => {
    if (diffPrice != null) {
      const _price = diffPrice + extraPrice;
      if (_price > 0) {
        const _txt = `${_price / 100}`;
        return ['+', _txt];
      }

      if (_price < 0) {
        const _txt = `${Math.abs(_price) / 100}`;
        return ['-', _txt];
      }
    }

    return ['+', '0'];
  }, [diffPrice, extraPrice]);

  return (
    <div className={styles.comboPrice}>
      <div>
        <span className={styles.comboPriceUnit}>
          {unit}&nbsp;{symbol}
        </span>
        {priceTxt && <span>{priceTxt}</span>}
      </div>
    </div>
  );
};

export type ComboItemProps = {
  className?: string;
  style?: CSSProperties;
  choicesType?: number;
  item?: ProductInfoComboProductType;
  itemExtraCustomization?: ProductInfoComboProductType['customization'];
  disabledExtraPrice?: boolean;
  /** for choicesType=1 */
  selected?: boolean;
  onCustom?: MouseEventHandler<HTMLDivElement>;
  onSelect?: MouseEventHandler<HTMLDivElement>;
};

export const ComboItem = memo(function ComboItem(props: ComboItemProps) {
  const {
    className,
    style,
    choicesType,
    item,
    itemExtraCustomization,
    disabledExtraPrice,
    selected,
    onCustom,
    onSelect,
  } = props;
  const sellout = useMemo(() => item?.sellout === true, [item?.sellout]);

  const tagImg = useMemo(
    () => (item?.tags && Array.isArray(item.tags) ? item.tags[0]?.image : undefined),
    [item?.tags],
  );

  const isDefault = useMemo(
    () => (choicesType === 0 && checkSingleComboProductIsDefault(item)) || selected,
    [choicesType, item, selected],
  );

  const extraPrice = useMemo(
    () => (disabledExtraPrice ? undefined : getCustomizationItemsTotalPrice(itemExtraCustomization?.items)),
    [disabledExtraPrice, itemExtraCustomization?.items],
  );

  const onCustomClick = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation();

      onCustom?.(e);
    },
    [onCustom],
  );
  const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      if (!sellout) {
        onSelect?.(e);
      }
    },
    [onSelect, sellout],
  );

  return (
    <div
      className={classNames(styles.comboItemWrapper, className)}
      style={style}
      data-selected={selected}
      data-choicestype={choicesType}
      data-sellout={sellout}
      onClick={handleClick}>
      {tagImg && <Image className={styles.comboTagImg} placeholder={null} src={tagImg} />}
      {sellout && <Image className={styles.comboSelloutImg} src={'https://img.mcd.cn/gallery/0d48166927fbe473.png'} />}
      <div className={styles.comboInfo}>
        <Image
          className={styles.comboImg}
          placeholder={null}
          fit="scale-down"
          fallback={<img className={styles.comboImgFallback} src={getCDNAssets('menu_list_default_img.png')} />}
          src={item?.image}
        />
        <div className={styles.comboName} data-default={isDefault}>
          {item?.name}
        </div>

        {choicesType === 1 && <PriceNode diffPrice={item?.diffPrice} extraPrice={extraPrice} />}
      </div>
      {!sellout && item?.hasCustomization === 1 && (choicesType === 0 || (choicesType !== 0 && selected)) && (
        <div className={styles.comboCustomBtn} onClick={onCustomClick}>
          定制
        </div>
      )}
      {/* custom button placeholder */}
      {item?.hasCustomization === 1 && choicesType !== 0 && !selected && (
        <div className={styles.comboCustomBtnPlaceholder} />
      )}
      {!sellout && choicesType === 1 && (
        <Image
          className={styles.comboSelectImg}
          placeholder={null}
          fallback={<img src={getCDNAssets('menu_list_default_img.png')} />}
          src={selected ? getCDNAssets('detail-item-sel.png') : getCDNAssets('detail-item-nor.png')}
        />
      )}
    </div>
  );
});

export type IncludeCombosProps = {
  className?: string;
  style?: CSSProperties;
  items?: ProductInfoComboItem[];
  onCustom?: (choicesType: number, combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
};

export const IncludeCombos: FC<IncludeCombosProps> = memo(function _IncludeCombos(props) {
  const { className, style, items, onCustom } = props;
  return (
    <div className={className} style={style}>
      <div className={styles.itemTitle}>已包含</div>
      <div className={styles.itemChoices}>
        {items?.map((c, index) =>
          c.comboProducts?.map((p) => (
            <ComboItem
              key={`combonoitem${String(p.code)}${index}`}
              className={styles.itemChoice}
              choicesType={0}
              item={p}
              onCustom={() => onCustom?.(0, c, p)}
            />
          )),
        )}
      </div>
    </div>
  );
});

export type ComboChoicesGroupProps = {
  className?: string;
  style?: CSSProperties;
  displayLength?: number;
  disabledExtraCustomPrice?: boolean;
  data?: ProductInfoComboItem;
  itemsExtraCustomizationObj?: Record<string, ProductInfoCustomization>;
  onSelect?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onCustom?: (choicesType: number, combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
};

export const ComboChoicesGroup: FC<ComboChoicesGroupProps> = memo(function _ComboChoicesGroup(props) {
  const {
    className,
    style,
    displayLength = MAX_DISPLAY_COMBO_LENGTH,
    disabledExtraCustomPrice,
    data,
    itemsExtraCustomizationObj,
    onSelect,
    onCustom,
  } = props;

  const [isFold, setFlod] = useState(true);
  const displayProducts = useMemo(
    () => (isFold && data ? data.comboProducts?.slice(0, displayLength) : data?.comboProducts),
    [data, displayLength, isFold],
  );
  const showMoreBtn = useMemo(
    () => data?.comboProducts && data.comboProducts.length > displayLength,
    [data?.comboProducts, displayLength],
  );

  const onFlodClick = useCallback(() => {
    setFlod((v) => !v);
  }, []);

  if (!data) {
    return null;
  }

  return (
    <div className={className} style={style}>
      <div className={styles.itemTitle}>{data.name}&nbsp;(单选)</div>
      <div className={styles.itemChoices} data-show-more={showMoreBtn}>
        {displayProducts?.map((p, pIndex) => {
          const extraCustomizationCacheKey = p.code && getExtraCustomizationCacheKey(data, p.code);
          const _customization =
            (extraCustomizationCacheKey &&
              itemsExtraCustomizationObj &&
              itemsExtraCustomizationObj[extraCustomizationCacheKey]) ||
            undefined;

          return (
            <ComboItem
              key={`combocanitempro${String(p.code)}${pIndex}`}
              className={styles.itemChoice}
              disabledExtraPrice={disabledExtraCustomPrice}
              choicesType={1}
              item={p}
              itemExtraCustomization={_customization}
              selected={checkSingleComboProductIsDefault(p)}
              onSelect={() => onSelect?.(data, p)}
              onCustom={() => onCustom?.(1, data, p)}
            />
          );
        })}
      </div>

      {showMoreBtn && (
        <div className={styles.moreWrapper}>
          <div className={styles.moreButton} onClick={onFlodClick}>
            {isFold ? '更多' : '收起'}
          </div>
        </div>
      )}
    </div>
  );
});

export type ComboDetailProps = {
  className?: string;
  style?: CSSProperties;
  items?: ProductInfoComboItem[];
  itemsExtraCustomizationObj?: Record<string, ProductInfoCustomization>;
  onSelect?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onCustom?: (choicesType: number, combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
};

const ComboDetail: FC<ComboDetailProps> = (props) => {
  const { className, style, items, itemsExtraCustomizationObj, onSelect, onCustom } = props;

  const [comboNoChoices, comboCanChoices] = useMemo(() => {
    const _noChoices: ProductInfoComboItem[] = [];
    const _canChoices: ProductInfoComboItem[] = [];
    items?.forEach((i) => {
      if (i.isChoices === 0) {
        _noChoices.push(i);
      } else {
        _canChoices.push(i);
      }
    });

    return [_noChoices, _canChoices];
  }, [items]);

  return (
    <div className={className} style={style}>
      {comboNoChoices && comboNoChoices.length > 0 && (
        <IncludeCombos className={styles.choicesWrapper} items={comboNoChoices} onCustom={onCustom} />
      )}

      {comboCanChoices &&
        comboCanChoices.length > 0 &&
        comboCanChoices.map((c, index) => (
          <ComboChoicesGroup
            key={`combocanitem${String(c.name)}${index}`}
            data={c}
            itemsExtraCustomizationObj={itemsExtraCustomizationObj}
            onCustom={onCustom}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
};

export default ComboDetail;
